import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

/**
 * Pageview das navegações internas para o GTM (AB#12099).
 *
 * O Volto troca de página sem recarregar, então o `gtm.js` só vê a primeira.
 * O gatilho nativo "Alteração do histórico" do GTM dispara antes de o
 * conteúdo novo chegar, com o `document.title` da página anterior. Aqui o
 * evento `mpf.pageview` só vai para o `dataLayer` depois que o conteúdo
 * carregou e o Helmet atualizou o título.
 *
 * No GTM: gatilho de evento personalizado `mpf.pageview` para a tag de
 * page_view do GA4, que lê `page_location` e `page_title` do `dataLayer`.
 *
 * Sem GTM na página (sem `dataLayer`), logado ou no backoffice, nada é
 * enviado. Registrado em `config.settings.appExtras`; não renderiza nada.
 */

export const PAGEVIEW_EVENT = 'mpf.pageview';

// O Helmet aplica o título num requestAnimationFrame depois do render.
export const TITLE_DELAY_MS = 300;

const BACKOFFICE_RE =
  /(^|\/)(edit|add|contents|sharing|history|diff|layout|controlpanel|login|logout|personal-information|change-password)(\/|$)/;

export const isBackoffice = (pathname: string) =>
  BACKOFFICE_RE.test(pathname) || /\/@@?[^/]+/.test(pathname);

const PageViewTracker = () => {
  const { pathname, search } = useLocation();
  const loading = useSelector((state: any) => !!state.content?.get?.loading);
  const token = useSelector((state: any) => state.userSession?.token);
  const page = `${pathname}${search}`;
  // A primeira página o `gtm.js` já conta.
  const lastPage = useRef(page);

  useEffect(() => {
    if (loading || page === lastPage.current) return undefined;

    // Se o conteúdo começar a carregar antes do prazo, o efeito é refeito e
    // o envio espera o fim da carga.
    const timer = window.setTimeout(() => {
      lastPage.current = page;
      const dataLayer = (window as any).dataLayer;
      if (token || isBackoffice(pathname) || !Array.isArray(dataLayer)) return;
      dataLayer.push({
        event: PAGEVIEW_EVENT,
        page_location: window.location.href,
        page_path: page,
        page_title: document.title,
      });
    }, TITLE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [page, pathname, loading, token]);

  return null;
};

export default PageViewTracker;
